import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import { adminService, UserInfo } from '../services/adminService';
import { USER_ROLE_VALUES } from '../constants/userRoles';
import { UserRoleValue } from '@/services/enums';

const UserList: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editingInvestedAmount, setEditingInvestedAmount] = useState<string>('');
  const [editingInvestedAt, setEditingInvestedAt] = useState<string>('');
  const loadingRef = useRef(false);

  const loadUsers = async () => {
    // 防止重复调用
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      const response = await adminService.getUserList(page, 20, search || undefined);
      setUsers(response.users);
      setTotalPages(response.pagination.totalPages);
    } catch (error: any) {
      console.error('获取用户列表失败:', error);
      showToast(error.message || '获取用户列表失败', 'error');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // 合并为一个 useEffect，避免重复调用
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const handleRoleChange = async (userId: number, newRole: UserRoleValue) => {
    if (updatingUserId) return;

    try {
      setUpdatingUserId(userId);
      await adminService.updateUserRole(userId, newRole);
      showToast('用户权限已更新', 'success');
      await loadUsers(); // 重新加载用户列表
    } catch (error: any) {
      console.error('更新用户权限失败:', error);
      showToast(error.message || '更新用户权限失败', 'error');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleStartEditInvestment = (user: UserInfo) => {
    setEditingUserId(user.id);
    setEditingInvestedAmount(user.investedAmount?.toString() || '');
    setEditingInvestedAt(user.investedAt ? new Date(user.investedAt).toISOString().split('T')[0] : '');
  };

  const handleCancelEditInvestment = () => {
    setEditingUserId(null);
    setEditingInvestedAmount('');
    setEditingInvestedAt('');
  };

  const handleSaveInvestment = async (userId: number) => {
    if (updatingUserId) return;

    try {
      setUpdatingUserId(userId);
      const investedAmount = editingInvestedAmount ? parseFloat(editingInvestedAmount) : null;
      const investedAt = editingInvestedAt || null;
      await adminService.updateUserInvestment(userId, investedAmount, investedAt);
      showToast('用户投资信息已更新', 'success');
      await loadUsers(); // 重新加载用户列表
      handleCancelEditInvestment();
    } catch (error: any) {
      console.error('更新用户投资信息失败:', error);
      showToast(error.message || '更新用户投资信息失败', 'error');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const getRoleLabel = (role: string) => {
    const roleMap: Record<string, string> = {
      USER: '普通用户',
      VIP: 'VIP用户',
      ADMIN: '管理员',
      SUPER_ADMIN: '超级管理员',
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role: string) => {
    const colorMap: Record<string, string> = {
      USER: 'text-white/60',
      VIP: 'text-yellow-400',
      ADMIN: 'text-blue-400',
      SUPER_ADMIN: 'text-red-400',
    };
    return colorMap[role] || 'text-white/60';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <GradientBackground>
      <ParticleBackground />
      <div className="min-h-screen">
        {/* 页面头部 */}
        <PageHeader title="用户列表" onBack={() => setCurrentPage('personal-center')} />

        {/* 内容区域 */}
        <div className="px-4 py-4 pt-20">
          {/* 搜索框 */}
          <div className="mb-4">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1); // 搜索时重置到第一页
              }}
              placeholder="搜索用户（Telegram ID、用户名、姓名）"
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/40"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-white/60 text-lg mb-2">暂无用户</div>
            </div>
          ) : (
            <>
              <div className="space-y-3 mb-4">
                {users.map((user) => (
                  <div key={user.id} className="p-4 rounded-xl border backdrop-blur-sm bg-white/5 border-white/10">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="text-white font-semibold mb-1">
                          {user.firstName} {user.lastName || ''}
                        </div>
                        {user.username && <div className="text-white/60 text-sm mb-1">@{user.username}</div>}
                        <div className="text-white/40 text-xs">Telegram ID: {user.telegramId}</div>
                      </div>
                      <div className={`text-sm font-semibold ${getRoleColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-3 text-sm text-white/60">
                      <span>创建时间: {formatDate(user.createdAt)}</span>
                      {user.lastLoginAt && <span>最后登录: {formatDate(user.lastLoginAt)}</span>}
                    </div>

                    {/* 投资信息 */}
                    {editingUserId === user.id ? (
                      <div className="space-y-2 mb-3 mt-3 p-3 bg-white/5 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white/80 w-20">投资金额:</span>
                          <input
                            type="number"
                            value={editingInvestedAmount}
                            onChange={(e) => setEditingInvestedAmount(e.target.value)}
                            placeholder="USDT"
                            className="flex-1 px-3 py-1 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-white/40"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white/80 w-20">投资时间:</span>
                          <input
                            type="date"
                            value={editingInvestedAt}
                            onChange={(e) => setEditingInvestedAt(e.target.value)}
                            className="flex-1 px-3 py-1 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-white/40"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveInvestment(user.id)}
                            disabled={updatingUserId === user.id}
                            className="flex-1 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 text-sm hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {updatingUserId === user.id ? '保存中...' : '保存'}
                          </button>
                          <button
                            onClick={handleCancelEditInvestment}
                            disabled={updatingUserId === user.id}
                            className="flex-1 px-3 py-1 bg-white/10 border border-white/20 rounded-lg text-white text-sm hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            取消
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3 p-2 bg-white/5 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-white/80">投资金额:</span>
                          <span className="text-sm text-white">
                            {user.investedAmount ? `${Number(user.investedAmount).toFixed(2)} USDT` : '未设置'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-white/80">投资时间:</span>
                          <span className="text-sm text-white">
                            {user.investedAt ? formatDate(user.investedAt) : '未设置'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/80">总收益:</span>
                          <span className="text-sm text-white">
                            {user.totalProfit !== undefined && user.totalProfit !== null
                              ? `${Number(user.totalProfit).toFixed(2)} USDT`
                              : '未设置'}
                          </span>
                        </div>
                        <button
                          onClick={() => handleStartEditInvestment(user)}
                          className="w-full mt-3 px-3 py-1 bg-white/10 border border-white/20 rounded-lg text-white text-sm hover:bg-white/20 transition-colors"
                        >
                          编辑投资信息
                        </button>
                      </div>
                    )}

                    {/* 权限选择 */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white/80">设置权限:</span>
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value as UserRoleValue)}
                        disabled={updatingUserId === user.id}
                        className="flex-1 px-3 py-1 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-white/40 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {USER_ROLE_VALUES.map((roleOption) => (
                          <option key={roleOption} value={roleOption} className="bg-slate-900">
                            {getRoleLabel(roleOption)}
                          </option>
                        ))}
                      </select>
                      {updatingUserId === user.id && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* 分页 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
                  >
                    上一页
                  </button>
                  <span className="text-white/80">
                    第 {page} / {totalPages} 页
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
                  >
                    下一页
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </GradientBackground>
  );
};

export default UserList;
